import { useState, useCallback } from 'react'
import { useAuthStore } from '../store'
import { saveAuthData } from '../../api/secureStore'

export interface UseUpdateNicknameOptions {
  onSuccess?: (displayName: string) => void
  onError?: (error: string) => void
}

export interface UseUpdateNicknameReturn {
  isUpdating: boolean
  updateNickname: (displayName: string) => Promise<void>
}

export function useUpdateNickname(
  options: UseUpdateNicknameOptions = {}
): UseUpdateNicknameReturn {
  const [isUpdating, setIsUpdating] = useState(false)

  const updateNickname = useCallback(
    async (displayName: string) => {
      const trimmed = displayName.trim()
      if (!trimmed) {
        options.onError?.('昵称不能为空')
        return
      }
      if (trimmed.length > 50) {
        options.onError?.('昵称不能超过50个字符')
        return
      }

      setIsUpdating(true)

      try {
        const { invoke } = await import('@tauri-apps/api/core')
        const user = useAuthStore.getState().user
        const accessToken = useAuthStore.getState().accessToken

        if (!user?.name || !accessToken) {
          throw new Error('未登录或登录已过期，请重新登录')
        }

        const result = await invoke<{
          success: boolean
          display_name: string
          message: string
        }>('update_nickname', {
          userName: user.name,
          displayName: trimmed,
          accessToken,
        })

        if (!result.success) {
          throw new Error(result.message || '昵称更新失败')
        }

        useAuthStore.getState().setUser({
          ...user,
          nickname: result.display_name,
        })

        const state = useAuthStore.getState()
        saveAuthData({
          accessToken: state.accessToken!,
          refreshToken: state.refreshToken!,
          expiresAt: state.expiresAt!,
          user: { ...user, nickname: result.display_name },
        }).catch(err =>
          console.warn('[UpdateNickname] 持久化昵称到 secureStore 失败:', err)
        )

        options.onSuccess?.(result.display_name)
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : '昵称更新失败'
        options.onError?.(errorMessage)
      } finally {
        setIsUpdating(false)
      }
    },
    [options]
  )

  return {
    isUpdating,
    updateNickname,
  }
}